import { AnimatePresence, motion } from 'framer-motion';
import { Pause, Play, Volume2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const MobileNarratorDrawer = ({ isOpen, onClose, mediators, resolveAsset, darkMode }) => {
  if (!mediators?.length) return null;

  const bg     = darkMode ? 'bg-[#0d0d14]' : 'bg-white';
  const border = darkMode ? 'border-white/10' : 'border-slate-200';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[900] bg-black/50 backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed bottom-0 left-0 right-0 z-[901] rounded-t-3xl border-t ${border} ${bg} overflow-hidden`}
            style={{ maxHeight: '75vh' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-white/20' : 'bg-slate-300'}`} />
            </div>

            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-3 border-b ${border}`}>
              <div className="flex items-center gap-3">
                <div className="relative w-5 h-5 flex items-center justify-center">
                  <span className={`absolute inline-flex w-full h-full rounded-full animate-ping ${
                    darkMode ? 'bg-blue-400/20' : 'bg-blue-300/40'
                  }`} />
                  <span className={`relative w-2 h-2 rounded-full ${
                    darkMode ? 'bg-blue-400' : 'bg-blue-600'
                  }`} />
                </div>
                <div>
                  <p className={`text-[9px] font-black uppercase tracking-[0.25em] font-mono ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>Neural_Narrator</p>
                  <p className={`text-[8px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {mediators.length} guide{mediators.length > 1 ? 's' : ''} actif{mediators.length > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all ${
                  darkMode ? 'border-white/10 text-slate-400 hover:bg-white/10' : 'border-slate-200 text-slate-500 hover:bg-slate-100'
                }`}
              >
                <X size={14} />
              </button>
            </div>

            {/* Contenu — on réutilise NeuralNarrator en mode "embedded" */}
            {/* On passe isOpen=true mais on l'enveloppe dans une div scrollable */}
            <div className="overflow-y-auto no-scrollbar" style={{ maxHeight: 'calc(75vh - 100px)' }}>
              {/*
                NeuralNarrator est positionné fixed normalement.
                Sur mobile on l'utilise différemment : on rend directement
                son contenu interne via un sous-composant sans le motion wrapper.
              */}
              <NarratorContent
                mediators={mediators}
                resolveAsset={resolveAsset}
                darkMode={darkMode}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ── Extraction du contenu interne du NeuralNarrator sans le wrapper fixed/drag ──


const WaveformBar = ({ isPlaying, index, accent }) => {
  const base  = 0.25 + Math.sin(index * 0.7) * 0.15;
  const delay = index * 0.04;
  const dur   = 0.6 + Math.sin(index * 1.3) * 0.2;
  return (
    <motion.div
      animate={isPlaying
        ? { scaleY: [base, base + 0.5, base + 0.8, base + 0.3, base] }
        : { scaleY: Math.max(base * 0.35, 0.12) }
      }
      transition={isPlaying
        ? { repeat: Infinity, duration: dur, delay, ease: 'easeInOut' }
        : { duration: 0.5 }
      }
      style={{ originY: '50%', height: 16, backgroundColor: accent, width: 2, borderRadius: 2 }}
    />
  );
};

const AudioPlayer = ({ src, darkMode }) => {
  const audioRef = useRef(null);
  const [playing,  setPlaying]  = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const progressRef = useRef(null);

  const accent    = darkMode ? '#60a5fa' : '#2563eb';
  const accentDim = darkMode ? 'rgba(96,165,250,0.15)' : 'rgba(37,99,235,0.1)';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime  = () => setProgress(audio.currentTime / audio.duration);
    const onMeta  = () => setDuration(audio.duration);
    const onEnded = () => { setPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else         { audio.play().catch(() => {}); setPlaying(true); }
  };

  const seek = (e) => {
    const bar  = progressRef.current;
    const audio = audioRef.current;
    if (!bar || !audio) return;
    const rect  = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    audio.currentTime = ratio * audio.duration;
    setProgress(ratio);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m   = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div className="space-y-3">
      <div
        className="w-full rounded-xl px-3 py-2 flex items-center justify-center cursor-pointer"
        style={{ background: accentDim, border: `1px solid ${accent}22` }}
        onClick={toggle}
      >
        <div className="flex items-center gap-[2px]" style={{ height: 20 }}>
          {[...Array(28)].map((_, i) => (
            <WaveformBar key={i} isPlaying={playing} index={i} accent={playing ? accent : (darkMode ? '#334155' : '#cbd5e1')} />
          ))}
        </div>
      </div>
      <div
        ref={progressRef}
        className="relative w-full h-1 rounded-full cursor-pointer"
        style={{ background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)' }}
        onClick={seek}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-full"
          style={{ width: `${progress * 100}%`, background: accent }}
        />
      </div>
      <div className="flex items-center justify-between">
        <button
          onClick={toggle}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all active:scale-95"
          style={{ background: playing ? accent : accentDim, color: playing ? '#fff' : accent, border: `1px solid ${accent}33` }}
        >
          {playing
            ? <><Pause size={11} fill="currentColor" /><span className="text-[9px] font-black uppercase tracking-widest">Pause</span></>
            : <><Play  size={11} fill="currentColor" /><span className="text-[9px] font-black uppercase tracking-widest">Écouter</span></>
          }
        </button>
        <div className="flex items-center gap-2">
          <Volume2 size={10} style={{ color: accent, opacity: 0.5 }} />
          <span className="text-[9px] font-mono" style={{ color: accent, opacity: 0.5 }}>
            {fmt(duration * progress)} / {fmt(duration)}
          </span>
        </div>
      </div>
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
};

const NarratorContent = ({ mediators, resolveAsset, darkMode }) => {
  const accent  = darkMode ? '#60a5fa' : '#2563eb';
  const border  = darkMode ? 'rgba(96,165,250,0.15)' : 'rgba(37,99,235,0.15)';
  const surface = darkMode ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)';
  const text    = darkMode ? '#cbd5e1' : '#374151';
  const muted   = darkMode ? '#475569' : '#9ca3af';

  return (
    <div className="p-4 space-y-4">
      {mediators.map((m, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08 }}
          className="rounded-2xl overflow-hidden"
          style={{ background: surface, border: `1px solid ${border}` }}
        >
          <div
            className="flex items-center gap-2 px-4 py-2.5"
            style={{ borderBottom: `1px solid ${border}` }}
          >
            <div
              className="w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-black font-mono"
              style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}30` }}
            >
              {String(i + 1).padStart(2, '0')}
            </div>
            <span className="text-[9px] font-black uppercase tracking-[0.2em] font-mono" style={{ color: muted }}>
              {m.type === 'audio' ? 'Message_Audio' : m.type === 'video' ? 'Message_Vidéo' : 'Note_Texte'}
            </span>
          </div>
          <div className="p-4 space-y-4">
            {m.description && (
              <p className="text-[12px] leading-relaxed font-sans italic" style={{ color: text }}>
                {m.description}
              </p>
            )}
            {m.type === 'audio' && m.path && (
              <AudioPlayer src={resolveAsset(m.path)} darkMode={darkMode} />
            )}
            {m.type === 'video' && m.path && (
              <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${border}` }}>
                <video src={resolveAsset(m.path)} controls className="w-full" style={{ background: '#000' }} />
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default MobileNarratorDrawer;